import React, { useState } from 'react';
import { useApp } from '@/contexts/AppContext';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { Download, FileText, Hash, ChevronRight, FileSignature, Pin, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AnimatedCounter from '@/components/apex/AnimatedCounter';
import PinToBundleDialog from '@/components/apex/PinToBundleDialog';
import RecordDrawer from '@/components/apex/RecordDrawer';
import ProofStatusBadge from '@/components/apex/ProofStatusBadge';
import ProvenanceDepthBar from '@/components/apex/ProvenanceDepthBar';
import { toast } from 'sonner';

function downloadPacket(API, investigationId, format) {
  const url = `${API}/evidence-packets/${investigationId}/${format}`;
  const a = document.createElement('a');
  a.href = url;
  a.download = `apex-packet-${investigationId}.${format}`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  toast.success(`${format.toUpperCase()} export started`, { description: investigationId });
}

export default function EvidenceVault() {
  const { seed, API } = useApp();
  const navigate = useNavigate();
  const [pinTarget, setPinTarget] = useState(null);
  const [activePacket, setActivePacket] = useState(null);
  if (!seed) return null;
  const s = seed.evidence_summary;

  const verifyPacket = (p) => {
    toast.success('Integrity verified', {
      description: 'Hash chain intact · All sources Object Lock confirmed · Coverage 98.7%',
      duration: 4000,
    });
  };

  const buildPacketRecord = (p) => ({
    idTag: p.investigation,
    title: `Evidence Packet · ${p.investigation}`,
    severity: 'enforced',
    subtitle: `Generated ${p.date} · Framework: ${p.framework} · ${p.scope}`,
    verdict: {
      tone: 'positive',
      label: 'Integrity verified',
      sub: 'All source records are present in immutable S3 storage. Hash chain intact. Evidence packet is audit-ready.',
    },
    signal: {
      tone: 'positive',
      lead: 'Auditor note:',
      body: 'This packet was generated from deterministic queries over immutable CloudTrail and Config data. Every finding in this packet can be independently verified by tracing the evidence ID to the source S3 partition.',
    },
    meta: [
      { label: 'Evidence ID', value: `EVD-${p.date}-${p.investigation}`, mono: true, sub: 'Append-only · S3 Object Lock' },
      { label: 'SHA-256', value: 'sha256:b4e7c2…f5', mono: true, sub: 'Notarised hourly' },
      { label: 'Sources', value: 'CloudTrail, Config, Inspector, Macie', sub: 'All Object Lock verified' },
      { label: 'Coverage', value: '98.7%', mono: true, tone: 'success', sub: 'Event coverage for observation window' },
      { label: 'Proof status', value: 'PROVEN', tone: 'success', mono: true, sub: '4 sources · hash chain intact' },
      { label: 'Provenance depth', value: '9.1 / 10', tone: 'success', mono: true, sub: 'CloudTrail + Config + Inspector + Macie' },
    ],
    sections: [
      {
        title: 'Evidence coverage',
        type: 'callout',
        callout: {
          tone: 'improving',
          title: '94.2% control coverage',
          lead: 'What this means:',
          body: '94.2% of required controls for this audit scope have complete evidence records. 3 controls have documented gaps with exception records. Auditors will receive a complete evidence packet with gap documentation — not an incomplete submission.',
        },
      },
      {
        title: 'Source breakdown',
        type: 'chart',
        chart: {
          kind: 'stack',
          label: 'Evidence records by source',
          sublabel: 'All sources Object Lock protected',
          total: 130594,
          segments: [
            { label: 'CloudTrail', value: 127847, color: '#0D9488' },
            { label: 'AWS Config', value: 1247,  color: '#0EA5E9' },
            { label: 'Inspector',  value: 1412,  color: '#8B5CF6' },
            { label: 'Macie',      value: 88,    color: '#EC4899' },
          ],
        },
      },
      {
        title: 'Framework control mapping',
        type: 'kv',
        items: [
          { k: 'SOC 2 CC6.1',         v: 'Covered · 847 events',                      tone: 'success'  },
          { k: 'SOC 2 CC8.1',         v: 'Gap documented · 7 pipeline bypasses',      tone: 'warning'  },
          { k: 'SOC 2 CC7.2',         v: 'Covered · GuardDuty export verified',       tone: 'success'  },
          { k: 'PCI DSS 10.2',        v: 'Covered · audit trail complete',            tone: 'success'  },
          { k: 'PCI DSS 6.3',         v: 'Gap · dev artifact in prod scope',          tone: 'critical' },
          { k: 'ISO 27001 A.9.4.2',   v: 'Covered · access evidence present',         tone: 'success'  },
          { k: 'ISO 27001 A.12.1.2',  v: 'Gap · 7 unapproved changes',                tone: 'warning'  },
          { k: 'DORA Art. 25',        v: 'Covered · change records present',          tone: 'success'  },
        ],
      },
      {
        title: 'Integrity verification',
        type: 'kv',
        items: [
          { k: 'Object Lock',       v: 'Confirmed · WORM',          tone: 'success' },
          { k: 'Hash verification', v: 'Passed at query time',      tone: 'success' },
          { k: 'Chain continuity',  v: 'No gaps detected',          tone: 'success' },
          { k: 'Retention window',  v: '7 years · Enterprise plan', tone: 'lens' },
        ],
      },
    ],
    actions: [
      { label: 'Download PDF',     primary: true, testId: 'vault-download-pdf',  onClick: () => downloadPacket(API, p.investigation, 'pdf')  },
      { label: 'Download JSON',                   testId: 'vault-download-json', onClick: () => downloadPacket(API, p.investigation, 'json') },
      { label: 'Verify integrity',                testId: 'vault-verify',         onClick: () => verifyPacket(p) },
    ],
  });

  return (
    <div className="space-y-6" data-testid="evidence-vault">
      <div>
        <span className="text-[10px] uppercase tracking-[0.18em] text-apex-lens font-semibold">Evidence · Vault</span>
        <h1 className="text-2xl font-bold tracking-tight mt-1">Signed. Hashed. Audit-ready.</h1>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Stat label="Total packets" value={s.total_packets} />
        <Stat label="Exported this quarter" value={s.exported_quarter} />
        <Stat label="Avg generation" value={s.avg_generation_seconds} suffix="s" />
      </div>

      {/* Recent packets */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-sm font-bold text-apex-ink">Recent Packets</h3>
          <button
            onClick={() => navigate('/lens/audit')}
            data-testid="vault-view-all"
            className="text-xs font-semibold text-apex-lens inline-flex items-center gap-1 hover:underline"
          >
            View in Audit Workspace
            <ChevronRight className="w-3 h-3" />
          </button>
        </div>
        <div className="divide-y divide-slate-100">
          {seed.evidence_packets.map((p, i) => {
            const isFlagship = p.investigation === 'INV-2026-042';
            return (
              <motion.div
                key={p.investigation + p.date}
                initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}
                onClick={() => setActivePacket(p)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActivePacket(p); } }}
                data-testid={isFlagship ? 'evidence-packet-INV-2026-042' : `packet-${p.investigation}`}
                className={`p-4 sm:p-5 cursor-pointer transition-all hover:bg-slate-50 hover:shadow-inner ${isFlagship ? 'bg-teal-50/40 hover:bg-teal-50/70 ring-1 ring-inset ring-apex-lens/30' : ''}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <FileSignature className="w-3.5 h-3.5 text-apex-lens" />
                      <span className="text-[10px] font-mono text-apex-neutral">{p.date}</span>
                      <span className="text-[10px] uppercase tracking-wider text-apex-success font-semibold">{p.status}</span>
                      {isFlagship && <span className="text-[10px] uppercase tracking-wider text-apex-lens font-semibold">· Flagship</span>}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="text-sm font-semibold text-apex-ink">{p.investigation}</h4>
                      <ProofStatusBadge status="PROVEN" size="xs" testId={`packet-${p.investigation}-proof`} />
                      <ProvenanceDepthBar depth={9.1} size="xs" testId={`packet-${p.investigation}-depth`} />
                    </div>
                    <p className="text-[12px] text-apex-neutral mt-0.5 truncate">{p.scope} · {p.framework}</p>
                  </div>
                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <Button onClick={() => verifyPacket(p)} variant="outline" data-testid={`packet-${p.investigation}-verify`} className="gap-1.5"><ShieldCheck className="w-3.5 h-3.5" />Verify</Button>
                    <Button onClick={() => setPinTarget(p.investigation)} variant="outline" data-testid={`packet-${p.investigation}-pin`} className="gap-1.5"><Pin className="w-3.5 h-3.5" />Pin</Button>
                    <Button onClick={() => downloadPacket(API, p.investigation, 'pdf')} className="bg-apex-lens hover:bg-apex-lens-dark text-white gap-1.5"><Download className="w-3.5 h-3.5" />Download</Button>
                  </div>
                </div>

                {/* Anatomy */}
                {isFlagship && (
                  <div className="mt-4 bg-white rounded-md border border-slate-200 p-4 font-mono text-[11px] leading-relaxed" onClick={(e) => e.stopPropagation()}>
                    <div className="text-[10px] uppercase tracking-wider text-apex-neutral mb-2">Packet Anatomy</div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-1">
                      <Field label="Packet ID" value="EVD-2026-04-21-INV-2026-042" />
                      <Field label="Generated" value="2026-04-21 14:32:00 UTC" />
                      <Field label="Hash (SHA-256)" value="b4e7c2d1f3a5b7c9e1d3f5a7b9c1e3f5a7b9c1e3f5a7b9c1e3f5a7b9c1e3f5" />
                      <Field label="Signing Key" value="apex-evidence-fabric-prod-2026-q2" />
                      <Field label="Events" value="847 CloudTrail records" />
                      <Field label="Frameworks" value="SOC 2 CC6.1 · PCI 10.2 · ISO 27001 A.9.4.2" />
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button data-testid={`packet-${p.investigation}-pdf`} onClick={() => downloadPacket(API, p.investigation, 'pdf')} variant="outline" size="sm" className="gap-1.5 text-xs"><FileText className="w-3 h-3" />Download PDF</Button>
                      <Button data-testid={`packet-${p.investigation}-json`} onClick={() => downloadPacket(API, p.investigation, 'json')} variant="outline" size="sm" className="gap-1.5 text-xs"><Hash className="w-3 h-3" />Download JSON</Button>
                      <button onClick={() => setActivePacket(p)} data-testid={`packet-${p.investigation}-view`} className="text-[11px] text-apex-lens inline-flex items-center gap-1 ml-auto self-center font-semibold hover:underline">
                        View full record <ChevronRight className="w-3 h-3" />
                      </button>
                      <button onClick={() => navigate(`/lens/investigations?id=${p.investigation}`)} className="text-[11px] text-apex-lens inline-flex items-center gap-1 self-center font-semibold hover:underline">
                        Open investigation <ChevronRight className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>

      <PinToBundleDialog
        open={!!pinTarget}
        onOpenChange={(o) => !o && setPinTarget(null)}
        investigationId={pinTarget}
      />

      <RecordDrawer
        open={!!activePacket}
        onOpenChange={(o) => !o && setActivePacket(null)}
        record={activePacket ? buildPacketRecord(activePacket) : null}
        testId="evidence-packet-drawer"
      />
    </div>
  );
}

function Stat({ label, value, suffix }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4">
      <div className="text-[10px] uppercase tracking-wider text-apex-neutral font-semibold">{label}</div>
      <div className="text-2xl font-bold tabular text-apex-ink mt-1"><AnimatedCounter value={value} />{suffix}</div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="flex gap-3">
      <span className="text-apex-neutral shrink-0 w-28 uppercase tracking-wider text-[10px]">{label}</span>
      <span className="text-apex-ink-2 truncate">{value}</span>
    </div>
  );
}
